import dayjs, { Dayjs } from 'dayjs';
import {
  DeviceGroupScheduleModel,
  WeekDayEnum
} from '@models/digital-signage/device-group-schedules.model.ts';
import { ScheduleDateRange } from '@modules/digital-signage/model/playlist-schedule.model.ts';
import {
  DATE_FORMAT,
  WEEK_DAYS
} from '@modules/digital-signage/utils/schedule.utils.ts';

export const formatDateKey = (date: Dayjs): string => date.format(DATE_FORMAT);

export const toDate = (value: Dayjs | null): string | null =>
  value?.isValid() ? formatDateKey(value) : null;

export const isEmptyOrValidDate = (value: Dayjs | null): boolean =>
  !value || value.isValid();

export const toWeekDay = (date: Dayjs): WeekDayEnum =>
  ((date.day() + 6) % 7) as WeekDayEnum;

export const sortDays = (days: WeekDayEnum[]): WeekDayEnum[] =>
  [...days].sort((a, b) => a - b);

export const toggleWeekDay = (
  days: WeekDayEnum[],
  day: WeekDayEnum
): WeekDayEnum[] =>
  days.includes(day)
    ? days.filter((item) => item !== day)
    : sortDays([...days, day]);

export const isScheduleDateEnded = (date: Dayjs | null): boolean =>
  !!date?.isValid() && date.isBefore(dayjs(), 'day');

export const isSavedScheduleEnded = (
  endDate: Dayjs | null,
  savedEndDate: Dayjs | null
): boolean =>
  isScheduleDateEnded(savedEndDate) && toDate(endDate) === toDate(savedEndDate);

export const getScheduleMinDate = (savedDate?: Dayjs | null): Dayjs =>
  savedDate?.isBefore(dayjs(), 'day') ? savedDate : dayjs().startOf('day');

export const getScheduleDates = ({
  startDate,
  endDate
}: Pick<
  DeviceGroupScheduleModel,
  'startDate' | 'endDate'
>): ScheduleDateRange => ({
  start: startDate?.slice(0, 10) ?? null,
  end: endDate?.slice(0, 10) ?? null
});

export const intersectDates = (
  a: ScheduleDateRange,
  b: ScheduleDateRange
): ScheduleDateRange | null => {
  const pick = (x: string | null, y: string | null, max: boolean) =>
    x && y ? (x > y === max ? x : y) : x || y;
  const start = pick(a.start, b.start, true);
  const end = pick(a.end, b.end, false);
  return start && end && start > end ? null : { start, end };
};

export const getWeekDaysInRange = ({
  start,
  end
}: ScheduleDateRange): WeekDayEnum[] => {
  if (!start || !end) return WEEK_DAYS;
  const length = Math.min(dayjs(end).diff(start, 'day') + 1, 7);
  return sortDays(
    Array.from({ length: Math.max(length, 0) }, (_, i) =>
      toWeekDay(dayjs(start).add(i, 'day'))
    )
  );
};

export const isDateInPeriod = (
  date: string,
  { start, end }: ScheduleDateRange
): boolean => (!start || start <= date) && (!end || end >= date);

export const getLastDayDate = (
  day: WeekDayEnum,
  { start, end }: ScheduleDateRange
): string | null => {
  if (!end) return null;
  const endDate = dayjs(end);
  const last = formatDateKey(
    endDate.subtract((toWeekDay(endDate) - day + 7) % 7, 'day')
  );
  return start && last < start ? null : last;
};

export const getNextDayDate = (
  day: WeekDayEnum,
  { start, end }: ScheduleDateRange,
  now: Dayjs
): string | null => {
  const tomorrow = now.add(1, 'day').startOf('day');
  const from =
    start && dayjs(start).isAfter(tomorrow) ? dayjs(start) : tomorrow;
  const next = formatDateKey(from.add((day - toWeekDay(from) + 7) % 7, 'day'));
  return end && next > end ? null : next;
};
